import { type ReactNode } from "react"
import { useTranslations } from "next-intl"
import { isSupportedSocialPlatform, SOCIAL_PLATFORMS } from "../constants/socialPlatforms"
import { type SocialLinkItem } from "../types/navigationContent"
import { getDefaultSocialLinks } from "../utils/getDefaultSocialLinks"
import { useNavigationContent } from "./useNavigationContent"

type ResolvedSocialLink = {
    key: string
    href: string
    label: string
    icon: ReactNode
}

/**
 * Social links ready to render, from Storyblok when available.
 *
 * The a11y label is resolved here rather than server-side: it is generated from
 * a translation when the editor left the field empty.
 */
export function useSocialLinks(): ResolvedSocialLink[] {
    const t = useTranslations("AppFooter")
    const { socialLinks } = useNavigationContent()

    const items: SocialLinkItem[] = socialLinks.length ? socialLinks : getDefaultSocialLinks()

    return items.reduce<ResolvedSocialLink[]>((resolved, { key, platform, href, label }) => {
        // The payload can outlive the registry: `/api/navigation` is cached at
        // the CDN, so a response built before a platform was removed from the
        // code can still reach a browser running the new bundle. The footer is
        // on every page, so an unknown platform must be skipped rather than
        // crash the whole site.
        if (!isSupportedSocialPlatform(platform)) {
            return resolved
        }

        const { name, icon } = SOCIAL_PLATFORMS[platform]

        resolved.push({
            key,
            href,
            label: label || t("socials", { socialMedia: name }),
            icon,
        })

        return resolved
    }, [])
}

useSocialLinks.messages = ["AppFooter"] as const
